import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import { FaBars, FaTimes } from "react-icons/fa";
import logo from "../assets/images/logo.png";

/* ================= GLOBAL RESET ================= */
const GlobalStyle = createGlobalStyle`
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    width: 100%;
    overflow-x: hidden;
    font-family: "Poppins", sans-serif;
  }
`;

const BRAND_GRADIENT = "linear-gradient(135deg, #5f2eea, #0a84ff)";

/* ================= NAVBAR ================= */
const Navbar = styled.nav`
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 28px;
  background: ${BRAND_GRADIENT};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);

  @media (max-width: 768px) {
    padding: 8px 16px;
  }
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
`;

const LogoImg = styled.img`
  height: 42px;
  width: 42px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  padding: 6px;
`;

const BrandName = styled.span`
  font-family: "Playfair Display", "Poppins", sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: 1px;
  background: linear-gradient(90deg, #ffffff, #dbeafe);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  @media (max-width: 900px) {
    display: none;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 8px 14px;
  border-radius: 20px;
  transition: 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

const LogoutBtn = styled.button`
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
`;

const Hamburger = styled.div`
  display: none;
  font-size: 22px;
  color: #fff;
  cursor: pointer;

  @media (max-width: 900px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  position: absolute;
  top: 64px;
  right: 0;
  width: 240px;
  background: ${BRAND_GRADIENT};
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (min-width: 901px) {
    display: none;
  }
`;

/* ================= COMPONENT ================= */
export default function AdminNavbar({ role, handleLogout }) {
  const [open, setOpen] = useState(false);

  const isAdmin = role === "Admin" || role === "Manager";
  const isCustomer = role === "Customer";

  const dashboardRoute = useMemo(() => {
    switch (role) {
      case "Sales":
        return "/pages/sales-dashboard";
      case "Marketing":
        return "/pages/marketing-dashboard";
      case "Employee":
        return "/pages/employee-dashboard";
      case "Customer":
        return "/pages/customer-dashboard";
      default:
        return "/pages/dashboard";
    }
  }, [role]);

  const close = () => setOpen(false);

  return (
    <>
      <GlobalStyle />

      <Navbar>
        <Brand to={dashboardRoute} onClick={close}>
          <LogoImg src={logo} alt="Berrybloomer logo" />
          <BrandName>Berrybloomer</BrandName>
        </Brand>

        {/* ================= DESKTOP NAV ================= */}
        <NavLinks>
          {isAdmin ? (
            <>
              <StyledLink to="/pages/dashboard">Dashboard</StyledLink>
              <StyledLink to="/pages/attendance">Attendance</StyledLink>
              <StyledLink to="/pages/leave-requests">
                Leave Requests
              </StyledLink>
              <StyledLink to="/pages/customer-queries">
                Customer Queries
              </StyledLink>
              <StyledLink to="/pages/admin/projects">
                Projects
              </StyledLink>

              {/* NEW CLIENT BUTTON */}
              <StyledLink to="/pages/client">Clients</StyledLink>
            </>
          ) : isCustomer ? (
            <StyledLink to={dashboardRoute}>Dashboard</StyledLink>
          ) : (
            <>
              <StyledLink to={dashboardRoute}>Dashboard</StyledLink>
              <StyledLink to="/pages/attendance">Attendance</StyledLink>
              <StyledLink to="/pages/apply-leave">Apply Leave</StyledLink>
            </>
          )}

          <LogoutBtn onClick={handleLogout}>Logout</LogoutBtn>
        </NavLinks>

        {/* ================= MOBILE TOGGLE ================= */}
        <Hamburger onClick={() => setOpen(!open)}>
          {open ? <FaTimes /> : <FaBars />}
        </Hamburger>

        {/* ================= MOBILE MENU ================= */}
        {open && (
          <MobileMenu>
            {isAdmin ? (
              <>
                <StyledLink to="/pages/dashboard" onClick={close}>
                  Dashboard
                </StyledLink>

                <StyledLink to="/pages/attendance" onClick={close}>
                  Attendance
                </StyledLink>

                <StyledLink to="/pages/leave-requests" onClick={close}>
                  Leave Requests
                </StyledLink>

                <StyledLink to="/pages/customer-queries" onClick={close}>
                  Customer Queries
                </StyledLink>

                <StyledLink to="/pages/admin/projects" onClick={close}>
                  Projects
                </StyledLink>

                {/* NEW CLIENT BUTTON */}
                <StyledLink to="/pages/client" onClick={close}>
                  Clients
                </StyledLink>
              </>
            ) : isCustomer ? (
              <StyledLink to={dashboardRoute} onClick={close}>
                Dashboard
              </StyledLink>
            ) : (
              <>
                <StyledLink to={dashboardRoute} onClick={close}>
                  Dashboard
                </StyledLink>

                <StyledLink to="/pages/attendance" onClick={close}>
                  Attendance
                </StyledLink>

                <StyledLink to="/pages/apply-leave" onClick={close}>
                  Apply Leave
                </StyledLink>
              </>
            )}

            <LogoutBtn
              onClick={() => {
                handleLogout();
                close();
              }}
            >
              Logout
            </LogoutBtn>
          </MobileMenu>
        )}
      </Navbar>
    </>
  );
}